import { API_BASE_URL } from './api';
import { authService } from './authService';
import { FreedivePersonalBests, UserPublicProfile } from './buddyService';

export interface UpdateOwnProfileRequest {
  bio?: string;
  specialtyTags?: string[];
  homeSiteId?: string;
  homeSiteName?: string;
  divingSinceYear?: number;
  freedivePersonalBests?: FreedivePersonalBests;
  /** Send an empty string to clear the nickname and revert to the real name. */
  nickname?: string;
}

export interface ProfilePhotoUploadUrlResponse {
  uploadUrl: string;
  photoKey: string;
}

const authJsonHeaders = () => ({ 'Content-Type': 'application/json', ...authService.getAuthHeaders() });

// Profile photos live in the same public bucket as post photos (see photoUrl() in
// postService.ts), just under a different key prefix — no new infra needed.
export const profilePhotoUrl = (photoKey: string): string =>
  `https://aidivecoach-post-photos.s3.us-east-2.amazonaws.com/${photoKey}`;

/** A region community's members, by tier. The lists are capped; the counts are the real totals. */
export interface RegionMembers {
  locals: UserPublicProfile[];
  joined: UserPublicProfile[];
  /** Divers whose home site is in another region of the same country (only when a country was asked for). */
  country: UserPublicProfile[];
  localCount: number;
  joinedCount: number;
}

/** A country's divers (everyone whose home site is in it). The list is capped; memberCount is the real total. */
export interface CountryMembers {
  members: UserPublicProfile[];
  memberCount: number;
}

export type RegionCounts = Record<string, { locals: number; joined: number }>;

export const userProfileService = {
  /** Reports which region (and country) the diver's home site is in, so the server's region index can list them as local. */
  setHomeRegion: async (regionSlug: string | null, countrySlug: string | null): Promise<void> => {
    const res = await fetch(`${API_BASE_URL}/users/home-region`, {
      method: 'PUT',
      headers: authJsonHeaders(),
      body: JSON.stringify({ regionSlug, countrySlug }),
    });
    if (!res.ok) throw new Error('Failed to update home region');
  },

  /** Reports the browser's IANA time zone so emails can show event times in it. */
  setTimeZone: async (timeZone: string): Promise<void> => {
    const res = await fetch(`${API_BASE_URL}/users/time-zone`, {
      method: 'PUT',
      headers: authJsonHeaders(),
      body: JSON.stringify({ timeZone }),
    });
    if (!res.ok) throw new Error('Failed to update time zone');
  },

  getRegionMembers: async (slug: string, countrySlug?: string): Promise<RegionMembers> => {
    const query = countrySlug ? `?country=${encodeURIComponent(countrySlug)}` : '';
    const res = await fetch(`${API_BASE_URL}/users/regions/${encodeURIComponent(slug)}/members${query}`, { headers: authJsonHeaders() });
    if (!res.ok) throw new Error('Failed to load community members');
    return res.json();
  },

  getCountryMembers: async (slug: string): Promise<CountryMembers> => {
    const res = await fetch(`${API_BASE_URL}/users/countries/${encodeURIComponent(slug)}/members`, { headers: authJsonHeaders() });
    if (!res.ok) throw new Error('Failed to load country members');
    return res.json();
  },

  /** Country slug → how many divers call it home. */
  getCountryCounts: async (): Promise<Record<string, number>> => {
    const res = await fetch(`${API_BASE_URL}/users/countries/counts`, { headers: authJsonHeaders() });
    if (!res.ok) throw new Error('Failed to load country counts');
    return res.json();
  },

  getRegionCounts: async (): Promise<RegionCounts> => {
    const res = await fetch(`${API_BASE_URL}/users/regions/counts`, { headers: authJsonHeaders() });
    if (!res.ok) throw new Error('Failed to load community counts');
    return res.json();
  },

  joinRegion: async (slug: string): Promise<void> => {
    const res = await fetch(`${API_BASE_URL}/users/regions/${encodeURIComponent(slug)}`, { method: 'PUT', headers: authJsonHeaders() });
    if (!res.ok) throw new Error((await res.text()) || 'Failed to join community');
  },

  leaveRegion: async (slug: string): Promise<void> => {
    const res = await fetch(`${API_BASE_URL}/users/regions/${encodeURIComponent(slug)}`, { method: 'DELETE', headers: authJsonHeaders() });
    if (!res.ok) throw new Error('Failed to leave community');
  },

  updateOwnProfile: async (request: UpdateOwnProfileRequest): Promise<void> => {
    const res = await fetch(`${API_BASE_URL}/users/profile`, {
      method: 'PUT',
      headers: authJsonHeaders(),
      body: JSON.stringify(request),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || 'Failed to update profile');
    }
  },

  getPhotoUploadUrl: async (contentType: string, kind?: 'cover'): Promise<ProfilePhotoUploadUrlResponse> => {
    const res = await fetch(`${API_BASE_URL}/users/profile-photo-upload-url`, {
      method: 'POST',
      headers: authJsonHeaders(),
      body: JSON.stringify({ contentType, kind }),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || 'Failed to get a photo upload URL');
    }
    return res.json();
  },

  // Uploads directly to S3 via the presigned PUT URL, then returns the photoKey so the
  // caller can render it immediately without waiting for a profile refetch.
  uploadPhoto: async (file: File, kind?: 'cover'): Promise<string> => {
    const { uploadUrl, photoKey } = await userProfileService.getPhotoUploadUrl(file.type, kind);
    const putRes = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    });
    if (!putRes.ok) throw new Error('Failed to upload photo');
    return photoKey;
  },
};
